// Some routes carry a credential in the URL path (password-reset tokens, co-host
// invitation UUIDs, purchase-approval magic links). PostHog puts the URL on every
// pageview, pageleave and autocapture event, so those segments are redacted before
// anything leaves the browser.
const SENSITIVE_PATH_PATTERNS = [
  [/(\/reset-password\/)[^/?#]+/gi, '$1:token'],
  [/(\/co-host\/setup-password\/)[^/?#]+/gi, '$1:invitationUUID'],
  // Anchored to the first path segment so a nested `/guest/` elsewhere is left alone.
  [
    /((?:^|:\/\/[^/]+)\/(?:owner|guest|merchant)\/)(?!(?:approve|deny|cancel)(?:[/?#]|$))[^/?#]+/gi,
    '$1:purchaseUUID',
  ],
];

// `public/index.html` strips the magic-link token (the whole fragment) on load, but the browser's
// navigation entry keeps it and `$web_vitals` reports that. Path patterns never see a fragment.
const MAGIC_LINK_PATH_PATTERN = /\/magic-link\/?(?:\?.*)?$/i;

// Stripe redirects back to CHECKOUT_DETAILS with the intent id and its client secret
// in the query string, so the same URL properties carry those too. Param names are
// kept and only the values are replaced, so the redirect still shows up in funnels.
const SENSITIVE_PARAM_NAMES = ['setup_intent', 'payment_intent'];

// Catches anything credential-shaped we have not enumerated, `*_client_secret` and
// `captchaToken` included. Over-redacting a param is harmless here; none of the query
// params this app actually reads (`query`, `intro`, `error`, `message`) match.
const SENSITIVE_PARAM_PATTERN = /(secret|token|code|password|signature)$/i;

const isSensitiveParam = (name) =>
  SENSITIVE_PARAM_NAMES.includes(name.toLowerCase()) || SENSITIVE_PARAM_PATTERN.test(name);

// `$referrer` comes from arbitrary external pages, so a malformed escape sequence is
// possible and `decodeURIComponent` throws on those. Param names are rarely encoded,
// so falling back to the raw name still matches.
const decodeParamName = (name) => {
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

const redactQueryString = (search) =>
  search
    .split('&')
    .map((pair) => {
      const separator = pair.indexOf('=');
      if (separator === -1) {
        return pair;
      }

      const name = pair.slice(0, separator);
      return isSensitiveParam(decodeParamName(name)) ? `${name}=:redacted` : pair;
    })
    .join('&');

// Split by hand rather than with `new URL()`: these values are sometimes relative
// (`$pathname`) or empty, and the URL constructor throws on both.
const redactUrl = (value) => {
  const hashStart = value.indexOf('#');
  if (hashStart !== -1) {
    const beforeHash = value.slice(0, hashStart);
    const fragment = value.slice(hashStart + 1);

    return (
      redactUrl(beforeHash) +
      '#' +
      (fragment && MAGIC_LINK_PATH_PATTERN.test(beforeHash) ? ':token' : redactUrl(fragment))
    );
  }

  const queryStart = value.indexOf('?');
  const path = queryStart === -1 ? value : value.slice(0, queryStart);
  const search = queryStart === -1 ? '' : value.slice(queryStart + 1);

  const redactedPath = SENSITIVE_PATH_PATTERNS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    path,
  );

  return search ? `${redactedPath}?${redactQueryString(search)}` : redactedPath;
};

// `href` catches autocapture's `attr__href` on the clicked element, `url` the `$external_click_url`
// that sits beside it.
const isUrlProperty = (key) => /(url|pathname|referrer|href)$/i.test(key);

// Autocapture also serialises the same elements into a single `$elements_chain` string, where the
// href appears as `href="..."`. Same match posthog-js itself uses to read the value back out.
const redactElementsChain = (chain) =>
  chain.replace(/href="(.*?)"/g, (match, href) => `href="${redactUrl(href)}"`);

const redactProperties = (properties) => {
  if (!properties) {
    return properties;
  }

  Object.keys(properties).forEach((key) => {
    const value = properties[key];

    if (typeof value === 'string' && isUrlProperty(key)) {
      properties[key] = redactUrl(value);
    } else if (typeof value === 'string' && key === '$elements_chain') {
      properties[key] = redactElementsChain(value);
    } else if (value && typeof value === 'object') {
      // Arrays included: `$elements` is one, and `$web_vitals` nests its URLs a level down.
      redactProperties(value);
    }
  });

  return properties;
};

export default function redactSensitiveUrls(event) {
  // posthog-js also puts `$set` and `$set_once` on the event itself, beside `properties`.
  [event?.properties, event?.$set, event?.$set_once].forEach(redactProperties);

  return event;
}

export { redactUrl };
